import { useEffect, useRef, useState } from "react";
import { CameraFlipIcon } from "../../Assets/assets";
import defaultFace from "../../Assets/face/defaultFace.svg";
import pitchDown from "../../Assets/face/pitchDown.gif";
import pitchUp from "../../Assets/face/pitchUp.gif";
import rollLeft from "../../Assets/face/rollLeft.gif";
import rollRight from "../../Assets/face/rollRight.gif";
import yawLeft from "../../Assets/face/yawLeft.gif";
import yawRight from "../../Assets/face/yawRight.gif";
import { setErrorStatus } from "../../Redux/status/action";
import {
  base64ToImageElement,
  cropBase64Image,
  rippleEffect
} from "../../Utils/constants";
import {
  isFaceInsideFrame,
  useFaceDetection
} from "../../CustomHooks/useFaceDetection";
import "./FaceIdentification.css";

export default function FaceDetectionAndCapture(props) {
  const [facingMode, setFacingMode] = useState("user");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const detectionLoading = useRef(false);
  const [detectionError, setDetectionError] = useState({
    status: null,
    message: "Keep your face within the frame",
    gif: defaultFace
  });
  const [capturedImage, setCapturedImage] = useState(null);
  const mainDivRef = useRef(null);
  const { humanInitialized, detectFace } = useFaceDetection();

  const startCamera = async () => {
    try {
      const constraints = {
        video: { facingMode },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (error) {
      console.error("Error accessing the camera:", error);
      alert("Could not access the camera. Please allow camera permissions.");
    }
  };

  const checkIsValidPhoto = async (detectedData, frameBox) => {
    console.log({
      angle: detectedData?.face?.[0]?.rotation?.angle,
      distance: detectedData?.face?.[0]?.distance,
      box: detectedData?.face?.[0]?.box
    });

    if (detectedData?.face?.length !== 1) {
      setDetectionError({
        status: null,
        message: "Please capture the subject's face for comparison",
        gif: defaultFace
      });
      return false;
    }

    const isInside = isFaceInsideFrame(detectedData?.face?.[0]?.mesh, frameBox);

    if (isInside !== true) {
      setDetectionError({
        status: "frameOut",
        message: "Face is outside the frame. Please align with the focus area",
        gif: defaultFace
      });
      return false;
    }

    if (detectedData?.face[0]?.distance > 0.7) {
      setDetectionError({
        status: "tooFar",
        message: "Face detected too far. Move closer to the focus area",
        gif: defaultFace
      });
      return false;
    }
    // else if (detectedData?.face[0]?.distance < 0.25) {
    //   setDetectionError({
    //     status: "tooClose",
    //     message: "Face detected too close. Move back to the focus area",
    //     gif: defaultFace
    //   });
    //   return false;
    // }

    const sortedAngles = Object.entries(
      detectedData.face[0].rotation.angle
    ).sort((a, b) => {
      return Math.abs(b[1]) - Math.abs(a[1]);
    });

    for (const angle of sortedAngles) {
      switch (angle[0]) {
        case "pitch":
          if (angle[1] < -0.1) {
            setDetectionError({
              status: "pitchUp",
              message: "Face too high. Please lower your head",
              gif: pitchUp
            });
            return false;
          } else if (angle[1] > 0.1) {
            setDetectionError({
              status: "pitchDown",
              message: "Face too low. Please raise your head",
              gif: pitchDown
            });
            return false;
          }
          break;

        case "yaw":
          if (angle[1] < -0.1) {
            setDetectionError({
              status: "yawLeft",
              message: "Face turned to left. Please straighten your head",
              gif: facingMode === "user" ? yawLeft : yawRight
            });
            return false;
          } else if (angle[1] > 0.1) {
            setDetectionError({
              status: "yawRight",
              message: "Face turned to right. Please straighten your head",
              gif: facingMode === "user" ? yawRight : yawLeft
            });
            return false;
          }
          break;

        case "roll":
          if (angle[1] < -0.1) {
            setDetectionError({
              status: "rollRight",
              message: "Face tilted to right. Please straighten your head",
              gif: facingMode === "user" ? rollRight : rollLeft
            });
            return false;
          } else if (angle[1] > 0.1) {
            setDetectionError({
              status: "rollLeft",
              message: "Face tilted to left. Please straighten your head",
              gif: facingMode === "user" ? rollLeft : rollRight
            });
            return false;
          }
          break;

        default:
          break;
      }
    }

    if (
      detectedData.face[0].emotion.find((data) => data.emotion === "neutral")
        ?.score < 0.4
    ) {
      setDetectionError({
        status: "emotion",
        message: "Please maintain a neutral expression",
        gif: defaultFace
      });
      return false;
    }

    setDetectionError({
      status: "good",
      message: "Hang on... Verifying face",
      gif: defaultFace
    });
    return true;
  };

  const captureImage = async () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const canvas = canvasRef.current;
      const video = videoRef.current;

      const context = canvas.getContext("2d");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = canvas.toDataURL("image/png");

      const { offsetWidth, offsetHeight } = mainDivRef.current;

      const cropX = offsetWidth / 2 - 350 / 2;
      const cropY = offsetHeight * 0.05;

      const frameBox = [cropX, cropY, 350, 350];

      const screenSizeImage = await cropBase64Image(
        imageData,
        (canvas.width - offsetWidth) / 2,
        (canvas.height - offsetHeight) / 2,
        offsetWidth,
        offsetHeight
      );

      const imageFile = await base64ToImageElement(screenSizeImage);
      const detectedData = await detectFace(imageFile);

      const isValidPhoto = await checkIsValidPhoto(detectedData, frameBox);

      if (isValidPhoto !== true) {
        detectionLoading.current = false;
        return;
      }

      const croppedImage = await cropBase64Image(
        screenSizeImage,
        cropX,
        cropY,
        350,
        350
      );

      if (props.selectedEmployee) {
        props.setCapturedPhoto(croppedImage);
        detectionLoading.current = false;
        props.navigate("/faceIdentification/uploadReference");
        return;
      }

      try {
        setCapturedImage(croppedImage);
        const result = await props.compareCapturedFace(croppedImage);

        if (!result?.status && result?.success === true) {
          props.setFaceComparisonData(result.data[0]);
          props.setCapturedPhoto(croppedImage);
          props.setCapturedId(result.capturedId);

          if (props.type === "notInLocation") {
            props.navigate("/faceIdentification/pending?type=notInLocation");
          } else {
            props.navigate("/faceIdentification/success");
          }
        } else if (!result?.status && result?.success === false) {
          props.setCapturedPhoto(croppedImage);
          props.setCapturedId(result.capturedId);
          props.setFaceComparisonData({
            body: {
              previewPhoto: croppedImage,
              createdAt: +new Date(),
              PCTNo: props.selectedLocation?.PCTNo,
              address: props.selectedLocation?.location
            }
          });
          if (props.type === "notInLocation") {
            props.navigate("/faceIdentification/failed?type=notInLocation");
          } else {
            props.navigate("/faceIdentification/failed");
          }
        } else if (props.isOnline === false && result?.status === "offline") {
          props.setFaceComparisonData({
            body: {
              previewPhoto: croppedImage,
              createdAt: +new Date(),
              PCTNo: props.selectedLocation?.PCTNo,
              address: props.selectedLocation?.location
            }
          });
          props.navigate("/faceIdentification/offline");
          setErrorStatus({
            code: "custom",
            message: "You're offline. We'll try again when you're back online."
          });
        } else {
          props.navigate("/");
        }
      } catch (error) {
        setCapturedImage(null);
        props.setCapturedPhoto(null);
        detectionLoading.current = false;
        //camera stuck without init
        switchCamera();
        switchCamera();
        setErrorStatus(error);
      }
      // const tracks = video.srcObject.getTracks();
      // tracks.forEach((track) => track.stop());
    } else {
      console.error("Camera is not initialized properly.");
    }
    detectionLoading.current = false;
  };

  useEffect(() => {
    let interval;
    if (humanInitialized) {
      interval = setInterval(async () => {
        try {
          if (detectionLoading.current === false) {
            detectionLoading.current = true;
            await captureImage();
          }
        } catch (error) {
          detectionLoading.current = false;
          console.error("error from faceDetection", error);
        }
      }, 1000);
    }

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [humanInitialized]);

  useEffect(() => {
    if (!humanInitialized) {
      return;
    }

    startCamera();
    const currentVideoRef = videoRef.current;

    return () => {
      if (currentVideoRef?.srcObject) {
        const tracks = currentVideoRef.srcObject.getTracks();
        tracks.forEach((track) => track.stop());
      }
    };
    // eslint-disable-next-line
  }, [facingMode, humanInitialized]);

  const switchCamera = async () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  return (
    <div
      ref={mainDivRef}
      className="inherit-parent-height inherit-parent-width position-relative"
    >
      {!capturedImage && (
        <>
          <video
            ref={videoRef}
            playsInline
            autoPlay
            muted
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              ...(facingMode === "user"
                ? {
                    transform: "scaleX(-1)",
                    WebkitTransform: "scaleX(-1)"
                  }
                : {})
            }}
          />
          <canvas ref={canvasRef} style={{ display: "none" }} />

          {/* <div
            style={{
              position: "absolute",
              border: `2px solid ${
                detectionError.status === "good" ? "#2965dd" : "#E2E2E2"
              }`,
              top: "5%",
              left: "50%",
              width: "350px",
              height: "350px",
              boxSizing: "border-box",
              transform: "translate(-50%, 0)",
              zIndex: 3
            }}
          ></div> */}

          {/* TOP white */}
          <div
            style={{
              position: "absolute",
              top: "0",
              width: "100%",
              height: "calc(5% + 15px)",
              background: "white",
              zIndex: 4
            }}
          />

          {/* LEFT white */}
          <div
            style={{
              position: "absolute",
              top: "0",
              left: "0",
              width: "calc((100% - 350px) / 2 + 15px)",
              height: "calc(5% + 350px)",
              background: "white",
              zIndex: 4
            }}
          />

          {/* RIGHT white */}
          <div
            style={{
              position: "absolute",
              top: "0",
              right: "0",
              width: "calc((100% - 350px) / 2 + 15px)",
              height: "calc(5% + 350px)",
              background: "white",
              zIndex: 4
            }}
          />

          {/* Capture Mark Box */}
          <>
            {/* TOP LEFT */}
            <CornerL
              height={20}
              width={20}
              position="absolute"
              top="5%"
              left="calc((100% - 350px) / 2)"
              zIndex={12}
            />

            {/* TOP RIGHT */}
            <CornerL
              height={20}
              width={20}
              position="absolute"
              top="5%"
              right="calc((100% - 350px) / 2)"
              zIndex={12}
              transform="rotate(90deg)"
            />

            {/* BOTTOM LEFT */}
            <CornerL
              height={20}
              width={20}
              position="absolute"
              top="calc(5% + 350px - 20px)"
              left="calc((100% - 350px) / 2)"
              zIndex={12}
              transform="rotate(-90deg)"
            />

            {/* BOTTOM RIGHT */}
            <CornerL
              height={20}
              width={20}
              position="absolute"
              top="calc(5% + 350px - 20px)"
              right="calc((100% - 350px) / 2)"
              zIndex={12}
              transform="rotate(180deg)"
            />
          </>

          <div
            className=" padding-large display-flex flex-direction-column flex-align-items-center"
            style={{
              position: "absolute",
              bottom: "0",
              left: "0",
              width: "100%",
              height: "calc(100% - (5% + 350px) + 15px)",
              background: "white",
              paddingTop: "70px",
              zIndex: 10
            }}
          >
            <img
              src={detectionError.gif}
              alt="face detecting"
              style={{
                height: "80px"
                // width: "48px"
              }}
            />

            <div
              className={`" padding-top-large font-size-small text-align-center ${
                detectionError.status === null ||
                detectionError.status === "good"
                  ? ""
                  : "font-color-red"
              }  `}
            >
              {detectionError.message}
            </div>
          </div>

          <div
            style={{
              position: "absolute",
              height: "64px",
              bottom: "0",
              zIndex: 20
            }}
            className="display-flex ripple-element flex-justify-content-center flex-align-items-center inherit-parent-width "
            onClick={(event) => {
              rippleEffect(event);
              switchCamera();
            }}
          >
            <CameraFlipIcon color="#1C1C1C" />

            <div
              style={{ color: "#1C1C1C" }}
              className=" padding-left-default font-size-medium"
            >
              Flip camera
            </div>
          </div>
        </>
      )}

      {capturedImage && (
        <div className="background-white inherit-parent-height inherit-parent-width">
          <div
            style={{
              paddingTop: "5%"
            }}
            className="inherit-parent-width flex-justify-content-center"
          >
            <div
              style={{
                width: "80%",
                position: "relative"
              }}
              className="flex-justify-content-center flex-align-items-center"
            >
              <div className="spinner-ring" />

              <img
                src={capturedImage}
                alt="test"
                style={{
                  width: "100%",
                  height: "100%",
                  padding: "10px",
                  borderRadius: "100%",
                  objectFit: "cover",
                  zIndex: 2
                }}
              />
            </div>
          </div>
          <div
            style={{
              color: "#0E0E0E"
            }}
            className="padding-top-larger text-align-center"
          >
            Verifying...
          </div>
        </div>
      )}
    </div>
  );
}

const CornerL = (props) => {
  return (
    <div
      style={{
        width: props.width || "21px",
        height: props.height || "21px",
        ...(props.position ? { position: props.position } : {}),
        ...(props.top ? { top: props.top } : {}),
        ...(props.bottom ? { top: props.bottom } : {}),
        ...(props.left ? { left: props.left } : {}),
        ...(props.right ? { right: props.right } : {}),
        ...(props.zIndex ? { zIndex: props.zIndex } : {}),
        ...(props.transform ? { transform: props.transform } : {}),
        ...(props.style ? props.style : {})
      }}
    >
      <svg
        width={props.width || "21"}
        height={props.height || "21"}
        viewBox="0 0 21 21"
        fill="none"
      >
        <path d="M21 1L0.999999 0.999999L0.999998 21" stroke="#6E6E6E" />
      </svg>
    </div>
  );
};
